import { Scene } from '@babylonjs/core/scene';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import { HighlightLayer } from '@babylonjs/core/Layers/highlightLayer';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateTube } from '@babylonjs/core/Meshes/Builders/tubeBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { Ability } from '../../systems/AbilitySystem';
import { COLORS } from '../../config/GameConfig';
import { AccessibilitySystem } from '../../systems/AccessibilitySystem';
import { a11yColor3 } from '../../systems/abilityColorHelpers';
import { gameEvents } from '../../core/EventBus';
import { GameEvents } from '../../config/events';

/**
 * InteractiveInfo — describes a registerable interactive object for AuraSight.
 */
export interface InteractiveInfo {
  /** The mesh representing this interactive */
  mesh: AbstractMesh;
  /** Optional visible mesh to highlight instead of the logical root. */
  highlightTarget?: AbstractMesh;
  /** Whether this object starts hidden (invisible until revealed) */
  hidden: boolean;
  /** Current state */
  state: 'hidden' | 'revealed' | 'solved';
  /** Called when revealed by Aura Sight */
  onReveal?: () => void;
  /** Called when concealed (Aura Sight deactivated) */
  onConceal?: () => void;
}

/** Level art tuning; reveal range, connection state and input stay unchanged. */
export interface AuraSightVisualConfig {
  glowIntensity?: number;
  threadRadius?: number;
  threadBrightness?: number;
}

/**
 * Aura Sight — Minna's perception ability (Babylon.js port).
 *
 * When active, reveals a hidden "energy overlay" layer showing:
 * - Hidden wires and connections between puzzle elements
 * - Mechanical weak points on obstacles
 * - Energy pulses flowing through the world
 *
 * Toggle: press to activate, press again to deactivate.
 * While active, interactive objects in range glow and hidden objects become visible.
 */
export class AuraSight implements Ability {
  readonly id = 'auraSight';
  readonly name = 'Aura Sight';
  isActive = false;
  cooldownRemaining = 0;
  readonly cooldownDuration = 0.5; // seconds

  private scene: Scene;
  private minnaMesh: AbstractMesh;
  private highlightLayer: HighlightLayer;
  private glowLayer: GlowLayer;
  private revealRadius = 10; // world units (Babylon scale)
  private elapsedTime = 0;
  private savedGlowIntensity = 0;

  /** Registered interactive objects in the scene */
  private interactives: InteractiveInfo[] = [];

  /** Pairs of connected objects to draw energy lines between */
  private connections: Array<[InteractiveInfo, InteractiveInfo]> = [];

  /** Active connection tube meshes (pooled — reused across frames) */
  private connectionTubeMeshes: Mesh[] = [];

  /**
   * Tracks current HighlightLayer membership + last color category per mesh.
   * 'solved' → emerald color; 'unsolved' → aura color.
   * Cleared on deactivate (after removeMesh calls) and on dispose.
   * Allows updateInteractiveHighlights to skip addMesh/removeMesh on
   * frames where membership and color category are unchanged.
   */
  private highlightMembership: Map<Mesh, 'solved' | 'unsolved'> = new Map();

  /** Materials for connection tubes (one per tube, for per-thread animation) */
  private connectionMaterials: StandardMaterial[] = [];

  /**
   * Scratch Color3 for the connection emissive loop — pre-allocated once,
   * mutated via .set() each frame instead of `new Color3(...)` per connection.
   */
  private readonly _scratchEmissive = new Color3();
  /** Scratch Color3 reused for diffuse black in connection material updates. */
  private readonly _scratchBlack = new Color3(0, 0, 0);

  /**
   * Tracks which tube indices have already been added to the glow layer so
   * we only call addIncludedOnlyMesh once per tube, not every frame.
   */
  private connectionTubeInGlow: boolean[] = [];

  /** Snapshot of connection visibility to detect changes */
  private lastVisibleConnectionKey = '';

  /**
   * Player-centered ambient glow particle system. Gives AuraSight
   * a visible cue even when no hidden interactives are nearby, so
   * activating the ability always produces feedback. Lives on the
   * Minna mesh as an emitter and is created lazily on first activation.
   */
  private ambientAuraPS: ParticleSystem | null = null;

  /**
   * Unsubscribe handle for the ACCESSIBILITY_CHANGED listener that
   * re-tints the ambient PS colors. Stored so we can remove it on
   * dispose and avoid a listener leak across level reloads.
   */
  private a11yUnsub: (() => void) | null = null;

  constructor(
    scene: Scene,
    minnaMesh: AbstractMesh,
    glowLayer: GlowLayer,
    highlightLayer: HighlightLayer,
    private readonly visualConfig: AuraSightVisualConfig = {},
  ) {
    this.scene = scene;
    this.minnaMesh = minnaMesh;
    this.glowLayer = glowLayer;
    this.highlightLayer = highlightLayer;

    // Subscribe once to ACCESSIBILITY_CHANGED so the ambient PS colors
    // re-tint live when the player switches colorblind mode mid-session.
    // The PS is long-lived (created lazily on first activation, kept
    // allocated through deactivations) so per-frame resolution is not
    // enough — we must re-tint reactively here too.
    this.a11yUnsub = gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, () => {
      this.retintAmbientPS();
    });
  }

  /**
   * Register interactive objects so Aura Sight can reveal/conceal them.
   */
  registerInteractives(objects: InteractiveInfo[]): void {
    this.interactives = objects;
  }

  /**
   * Register connections between puzzle elements for energy line rendering.
   */
  registerConnections(connections: Array<[InteractiveInfo, InteractiveInfo]>): void {
    this.connections = connections;
  }

  setEffectLayers(glowLayer: GlowLayer, highlightLayer: HighlightLayer): void {
    this.glowLayer = glowLayer;
    this.highlightLayer = highlightLayer;
  }

  /**
   * Enable or disable the overlay layers without changing the active state.
   * Used by the EnergySystem to create a flicker effect when Minna is depleted.
   * @deprecated Use setDepletionIntensity() instead — avoids per-frame render-pass attach/detach.
   */
  setOverlayEnabled(enabled: boolean): void {
    if (!this.isActive) return;
    this.highlightLayer.isEnabled = enabled;
  }

  /**
   * Modulate the depletion visual continuously (0..1) without toggling the
   * HighlightLayer on/off.  Keeps the layer enabled and drives two cheap
   * per-frame writes instead of an expensive render-pass attach/detach:
   *
   *   1. blurHorizontalSize / blurVerticalSize — scales the highlight blur
   *      spread from tight (0.3) at t=0 to the normal active width (1.2) at
   *      t=1, giving the "unstable aura collapsing inward" look.
   *   2. ambientAuraPS.emitRate — dims the ambient particle cloud proportionally.
   *
   * Call every frame during depleted-active state:
   *   - t = 0.5 + 0.5 * Math.sin(sceneTime * 15)  — pulsing unstable aura
   *   - t = 0.5                                     — reducedMotion steady dim
   *
   * highlightLayer.isEnabled is NEVER touched here — it stays true.
   *
   * @param t  Normalized intensity in [0, 1].  0 = nearly invisible, 1 = full.
   */
  setDepletionIntensity(t: number): void {
    if (!this.isActive) return;
    // Blur size: oscillates between 0.3 (collapsed) and 1.2 (full active).
    const blur = 0.3 + t * 0.9;
    this.highlightLayer.blurHorizontalSize = blur;
    this.highlightLayer.blurVerticalSize = blur;
    // Ambient particle rate: scale between 0 and the normal active rate (65).
    if (this.ambientAuraPS) {
      this.ambientAuraPS.emitRate = Math.round(t * 65);
    }
    // highlightLayer.isEnabled is intentionally NOT touched — uniform writes only.
  }

  canActivate(): boolean {
    return !this.isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this.isActive = true;
    this.elapsedTime = 0;
    this.savedGlowIntensity = this.glowLayer.intensity;

    // Enable cached layers with boosted spread for a magical look
    this.highlightLayer.isEnabled = true;
    this.highlightLayer.blurHorizontalSize = 1.2;
    this.highlightLayer.blurVerticalSize = 1.2;
    this.glowLayer.intensity = this.visualConfig.glowIntensity ?? 0.8;

    // Reveal hidden objects in range
    this.revealObjectsInRange();

    // Ambient player-centered glow. Always-on while active so the
    // ability has visible feedback even on levels where nothing in
    // range is hidden (e.g. L3 Clockwork Canopy, where apex interactives
    // register as state:"revealed" and the highlight pass alone is
    // not visible).
    this.ensureAmbientAura();
    if (this.ambientAuraPS) this.ambientAuraPS.emitRate = 65;

    return true;
  }

  private ensureAmbientAura(): void {
    if (this.ambientAuraPS) return;
    const ps = new ParticleSystem("auraSightAmbient", 140, this.scene);
    ps.emitter = this.minnaMesh;
    ps.minEmitBox = new Vector3(-0.4, -0.1, -0.4);
    ps.maxEmitBox = new Vector3(0.4, 0.55, 0.4);
    ps.minLifeTime = 0.6;
    ps.maxLifeTime = 1.2;
    ps.minSize = 0.15;
    ps.maxSize = 0.35;
    ps.emitRate = 0;
    ps.direction1 = new Vector3(-0.3, 0.4, -0.3);
    ps.direction2 = new Vector3(0.3, 0.8, 0.3);
    ps.minEmitPower = 0.2;
    ps.maxEmitPower = 0.5;
    ps.gravity = new Vector3(0, 0.2, 0);
    ps.colorDead = new Color4(0.05, 0.1, 0.15, 0);
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;
    ps.start();
    this.ambientAuraPS = ps;
    // Apply current a11y tint immediately so the first emission frame
    // already uses the correct palette.
    this.retintAmbientPS();
  }

  /**
   * Re-apply the current colorblind palette to the ambient PS color1/color2.
   * Called on creation and whenever ACCESSIBILITY_CHANGED fires.
   * NullEngine-safe: only writes if ambientAuraPS is non-null.
   */
  private retintAmbientPS(): void {
    const ps = this.ambientAuraPS;
    if (!ps) return;
    const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
    const auraColor = a11yColor3('MINNA_AURA', COLORS.MINNA_AURA, rc);
    ps.color1 = new Color4(auraColor.r, auraColor.g, auraColor.b, 0.85);
    ps.color2 = new Color4(auraColor.r * 0.6, auraColor.g, auraColor.b * 0.8, 0.55);
  }

  update(deltaTime: number): void {
    if (!this.isActive) return;

    this.elapsedTime += deltaTime;

    // Pulsing intensity
    const pulse = Math.sin(this.elapsedTime * 3) * 0.3 + 0.7;

    if (this.glowLayer) {
      // Keep the level's treatment through every pulse, not only activation.
      // With no override the original 0.8–1.1 range is unchanged.
      const levelScale = (this.visualConfig.glowIntensity ?? 0.8) / 0.8;
      this.glowLayer.intensity = (0.6 + pulse * 0.5) * levelScale;
    }

    // Update interactive highlights based on distance
    this.updateInteractiveHighlights();

    // Redraw connection lines with animated energy flow
    this.drawConnectionLines(pulse);
  }

  deactivate(): void {
    this.isActive = false;
    this.cooldownRemaining = this.cooldownDuration;

    // Restore pre-activate glow intensity (shared layer — don't leave our pulse value)
    this.glowLayer.intensity = this.savedGlowIntensity;

    // Silence the ambient aura particle system — stays allocated so
    // the next activation is instant.
    if (this.ambientAuraPS) this.ambientAuraPS.emitRate = 0;

    // Disable layers (keep allocated for reuse)
    this.highlightLayer.isEnabled = false;
    for (const obj of this.interactives) {
      const highlightMesh = this.getHighlightMesh(obj);
      if (highlightMesh) {
        this.highlightLayer.removeMesh(highlightMesh);
      }
    }
    // Clear membership tracking — all meshes have been removed above.
    this.highlightMembership.clear();

    // Hide connection tube meshes (don't dispose — reuse next activation)
    for (const mesh of this.connectionTubeMeshes) {
      mesh.setEnabled(false);
      // Return the shared layer to default-include mode — leaving tubes in
      // _includedOnlyMeshes silences every other emissive (crystals, portal,
      // Minna's eyes) for the rest of the level. removeIncludedOnlyMesh is a
      // no-op for tubes never added.
      this.glowLayer.removeIncludedOnlyMesh(mesh);
    }
    this.lastVisibleConnectionKey = '';
    // Reset glow-membership tracking so tubes are re-added on next activation
    this.connectionTubeInGlow = [];

    // Conceal hidden objects
    for (const obj of this.interactives) {
      if (obj.hidden && obj.state !== 'solved') {
        obj.state = 'hidden';
        obj.mesh.isVisible = false;
        obj.onConceal?.();
      }
    }
  }

  /**
   * Full cleanup — dispose pooled geometry and remove the
   * ACCESSIBILITY_CHANGED listener to prevent a listener leak
   * across level reloads.
   */
  dispose(): void {
    // Remove the a11y listener first so no callbacks fire on disposed resources.
    if (this.a11yUnsub) {
      this.a11yUnsub();
      this.a11yUnsub = null;
    }
    if (this.ambientAuraPS) {
      this.ambientAuraPS.stop();
      this.ambientAuraPS.dispose();
      this.ambientAuraPS = null;
    }
    this.disposeConnectionLines();
  }

  private revealObjectsInRange(): void {
    const minnaPos = this.minnaMesh.position;

    for (const obj of this.interactives) {
      const dist = Vector3.Distance(minnaPos, obj.mesh.position);
      if (dist <= this.revealRadius * 1.2) {
        obj.state = 'revealed';
        obj.mesh.isVisible = true;
        obj.onReveal?.();
      }
    }
  }

  private updateInteractiveHighlights(): void {

    const minnaPos = this.minnaMesh.position;
    const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
    const auraColor = a11yColor3('MINNA_AURA', COLORS.MINNA_AURA, rc);
    const emeraldColor = a11yColor3('EMERALD', COLORS.EMERALD, rc);

    for (const obj of this.interactives) {
      const dist = Vector3.Distance(minnaPos, obj.mesh.position);
      const highlightMesh = this.getHighlightMesh(obj);

      if (dist <= this.revealRadius) {
        // In range: reveal and highlight
        const isSolved = obj.state === 'solved';
        if (!isSolved) {
          obj.state = 'revealed';
        }
        obj.mesh.isVisible = true;
        obj.onReveal?.();

        if (highlightMesh) {
          const category: 'solved' | 'unsolved' = isSolved ? 'solved' : 'unsolved';
          const current = this.highlightMembership.get(highlightMesh);
          // Only call addMesh when entering range for the first time OR
          // when the color category changes (solved ↔ unsolved).
          if (current !== category) {
            const color = isSolved ? emeraldColor : auraColor;
            this.highlightLayer.addMesh(highlightMesh, color);
            this.highlightMembership.set(highlightMesh, category);
          }
        }
      } else {
        // Out of range: remove highlight only if currently tracked as a member.
        if (highlightMesh && this.highlightMembership.has(highlightMesh)) {
          this.highlightLayer.removeMesh(highlightMesh);
          this.highlightMembership.delete(highlightMesh);
        }
        if (obj.hidden && obj.state !== 'solved') {
          obj.state = 'hidden';
          obj.mesh.isVisible = false;
          obj.onConceal?.();
        }
      }
    }
  }

  /**
   * Build a wavy path between two points to simulate organic energy threads.
   * Uses a sine-displaced catmull-rom-style polyline perpendicular to the
   * connection direction, with time-varying phase for a living shimmer.
   */
  private buildThreadPath(posA: Vector3, posB: Vector3, time: number): Vector3[] {
    const segments = 24;
    const points: Vector3[] = [];
    const dir = posB.subtract(posA);
    const len = dir.length();
    const dirN = dir.normalize();

    // Build a perpendicular vector for the wave displacement
    const up = Math.abs(Vector3.Dot(dirN, Vector3.Up())) > 0.9
      ? Vector3.Right()
      : Vector3.Up();
    const perp1 = Vector3.Cross(dirN, up).normalize();
    const perp2 = Vector3.Cross(dirN, perp1).normalize();

    const waveAmp = Math.min(len * 0.06, 0.35);

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const base = Vector3.Lerp(posA, posB, t);
      // Two sine waves on perpendicular axes, with time-varying phase
      const envelope = Math.sin(t * Math.PI); // taper at endpoints
      const wave1 = Math.sin(t * Math.PI * 5 + time * 2.5) * waveAmp * envelope;
      const wave2 = Math.cos(t * Math.PI * 3.7 + time * 1.8) * waveAmp * 0.6 * envelope;
      points.push(
        base.add(perp1.scale(wave1)).add(perp2.scale(wave2)),
      );
    }
    return points;
  }

  private drawConnectionLines(pulse: number): void {
    const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
    const emeraldColor = a11yColor3('EMERALD', COLORS.EMERALD, rc);
    const greenColor = a11yColor3('BIOLUMINESCENT_GREEN', COLORS.BIOLUMINESCENT_GREEN, rc);

    // Build a key representing which connections are currently visible,
    // so we only recreate tube meshes when visibility/identity changes.
    let visibleKey = '';
    for (let ci = 0; ci < this.connections.length; ci++) {
      const [objA, objB] = this.connections[ci];
      if (objA.state !== 'hidden' && objB.state !== 'hidden') {
        visibleKey += `${ci}:${objA.state}:${objB.state},`;
      }
    }

    const connectionsChanged = visibleKey !== this.lastVisibleConnectionKey;

    if (connectionsChanged) {
      this.lastVisibleConnectionKey = visibleKey;

      // Hide all existing tube meshes — they will be re-shown below as needed.
      // We do NOT dispose them; existing updatable tubes will be reused via the
      // instance-update path. Only tubes whose identity has genuinely changed
      // will be disposed+recreated below.
      for (const mesh of this.connectionTubeMeshes) {
        mesh.setEnabled(false);
      }
      // Reset glow tracking so re-enabled tubes get re-added to the glow layer
      this.connectionTubeInGlow = [];
    }

    // Update tube geometry in place every frame (path animates with time).
    // buildThreadPath always returns segments+1 = 25 points, so the point
    // count is constant — CreateTube({instance}) can safely update in place.
    let meshIdx = 0;
    for (const [objA, objB] of this.connections) {
      if (objA.state === 'hidden' || objB.state === 'hidden') continue;

      const posA = objA.mesh.position;
      const posB = objB.mesh.position;
      const isSolved = objA.state === 'solved' && objB.state === 'solved';
      const baseColor = isSolved ? emeraldColor : greenColor;
      const threadPath = this.buildThreadPath(posA, posB, this.elapsedTime);
      const tubeRadius = this.visualConfig.threadRadius ?? 0.04;

      let tube: Mesh;
      if (meshIdx < this.connectionTubeMeshes.length) {
        const existing = this.connectionTubeMeshes[meshIdx];
        // Update vertex buffers in place — no new Mesh allocation, no GC.
        // Requires the tube was created with updatable:true and the path
        // point count is identical (both guaranteed here: 25 points always).
        CreateTube(`auraThread_${meshIdx}`, {
          path: threadPath,
          instance: existing,
        }, this.scene);
        existing.setEnabled(true);
        tube = existing;
      } else {
        // First time this slot is needed — allocate with updatable:true.
        tube = CreateTube(`auraThread_${meshIdx}`, {
          path: threadPath,
          radius: tubeRadius,
          tessellation: 6,
          cap: 0, // no caps
          updatable: true,
        }, this.scene);
        tube.isPickable = false;
        this.connectionTubeMeshes.push(tube);
      }

      // Material: reuse or create (never per-frame allocated)
      let mat: StandardMaterial;
      if (meshIdx < this.connectionMaterials.length) {
        mat = this.connectionMaterials[meshIdx];
      } else {
        mat = new StandardMaterial(`auraThreadMat_${meshIdx}`, this.scene);
        mat.disableLighting = true;
        mat.backFaceCulling = false;
        mat.alpha = 0.85;
        this.connectionMaterials.push(mat);
      }

      // Animate emissive intensity with pulse + per-thread phase offset.
      // Reuse pre-allocated scratch Color3 instances — no per-frame heap allocation.
      const threadPhase = meshIdx * 1.7;
      const shimmer = Math.sin(this.elapsedTime * 4 + threadPhase) * 0.3 + 0.7;
      const intensity = shimmer * pulse * (this.visualConfig.threadBrightness ?? 1);

      this._scratchEmissive.set(
        baseColor.r * intensity,
        baseColor.g * intensity,
        baseColor.b * intensity,
      );
      mat.emissiveColor.copyFrom(this._scratchEmissive);
      mat.diffuseColor.copyFrom(this._scratchBlack);
      mat.alpha = 0.5 + intensity * 0.4;

      tube.material = mat;

      // Add to GlowLayer ONCE per tube — not every frame.
      if (!this.connectionTubeInGlow[meshIdx]) {
        this.glowLayer.addIncludedOnlyMesh(tube);
        this.connectionTubeInGlow[meshIdx] = true;
      }

      meshIdx++;
    }

    // Disable any leftover pooled meshes beyond the current visible count
    for (let i = meshIdx; i < this.connectionTubeMeshes.length; i++) {
      this.connectionTubeMeshes[i].setEnabled(false);
    }
  }

  private getHighlightMesh(obj: InteractiveInfo): Mesh | null {
    const target = obj.highlightTarget ?? obj.mesh;
    return target instanceof Mesh ? target : null;
  }

  /** Dispose all pooled connection tube meshes and materials (call on full cleanup). */
  private disposeConnectionLines(): void {
    for (const mesh of this.connectionTubeMeshes) {
      // Restore the shared layer's default include-all behavior before
      // disposing — mirrors deactivate(); no-op for tubes never added.
      this.glowLayer.removeIncludedOnlyMesh(mesh);
      mesh.dispose();
    }
    for (const mat of this.connectionMaterials) {
      mat.dispose();
    }
    this.connectionTubeMeshes = [];
    this.connectionMaterials = [];
    this.connectionTubeInGlow = [];
    this.lastVisibleConnectionKey = '';
  }
}
